import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import powerDialerStore from "Stores/powerDialerStore";
import ToggleBtnType from "Components/common/ToggleBtnType";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { startWavRecording } from "Utils/wavRecorder";

const MAX_SECONDS = 90;

const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const Toggle = ({ id, label, desc, value, onChange }) => (
  <div className="pdRow">
    <div>
      {label}
      {desc && <div className="pdDesc">{desc}</div>}
    </div>
    <ToggleBtnType id={id} defaultValue={!!value} cbk={onChange} />
  </div>
);

// Inline icons (from the design): the bundled icon font lacks some glyphs.
const ICON_PATHS = {
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  alert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6M12 16.5v.5" />
    </>
  ),
  trash: <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />,
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </>
  ),
  upload: <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
};
const Icon = ({ name, size = 16 }) => (
  <svg className="pdIcon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICON_PATHS[name]}
  </svg>
);
const Glyph = ({ name, size = 14 }) => (
  <svg className="pdIcon" width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    {name === "play" && <path d="M4 2.5v11l9-5.5z" fill="currentColor" />}
    {name === "pause" && <path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" fill="currentColor" />}
    {name === "stop" && <rect x="3" y="3" width="10" height="10" rx="2" fill="currentColor" />}
  </svg>
);

// Length zones from the design: up to 0:30 ideal, 0:31-1:00 long, 1:01-1:30 too long.
const ZONES = {
  ideal: { label: "Ideal", icon: "check", tip: "Within the ideal 30 seconds" },
  long: { label: "Long", icon: "clock", tip: "Longer than the recommended 30 seconds" },
  tooLong: { label: "Too long", icon: "alert", tip: "Over 1 minute. Too long for a cold voicemail" }
};
const zoneOf = s => (s <= 30 ? "ideal" : s <= 60 ? "long" : "tooLong");

const recordingHint = s => {
  const zone = zoneOf(s);
  if (zone === "tooLong") return `Too long for a cold voicemail. Stops on its own in ${MAX_SECONDS - s} seconds.`;
  if (zone === "long") return "Past 0:30. Prospects often stop listening around here.";
  return s >= 20 ? "Ideal length. Wrap up in the next few seconds." : "Ideal length.";
};

const LengthBadge = ({ seconds }) => {
  const zone = zoneOf(seconds);
  return (
    <span className={`pdLen ${zone}`} title={ZONES[zone].tip}>
      <Icon name={ZONES[zone].icon} size={13} />
      {fmt(seconds)} · {ZONES[zone].label}
    </span>
  );
};

const RecordingProgress = ({ seconds, onStop, onCancel }) => {
  const zone = zoneOf(seconds);
  return (
    <div className="pdRecorder">
      <div className="pdRecorderHead">
        <span className="pdRecDot" />
        <span className="pdRecorderTitle">Recording</span>
        <span className={`pdRecorderTime ${zone}`}>
          {fmt(seconds)} <span>/ {fmt(MAX_SECONDS)}</span>
        </span>
      </div>
      <div className="pdRecBar">
        <span className="ideal" />
        <span className="long" />
        <span className="tooLong" />
        <i className={zone} style={{ "--pdRecFill": `${Math.min(100, (seconds / MAX_SECONDS) * 100)}%` }} />
      </div>
      <div className="pdRecTicks">
        <span>0:00</span>
        <span>0:30</span>
        <span>1:00</span>
        <span>1:30 max</span>
      </div>
      <div className="pdRecorderFoot">
        <button type="button" className="pdRecBtn primary" onClick={onStop}>
          <Glyph name="stop" />Stop
        </button>
        <button type="button" className="pdRecBtn" onClick={onCancel}>
          Cancel
        </button>
        <p className={`pdRecHint ${zone}`}>{recordingHint(seconds)}</p>
      </div>
    </div>
  );
};

const Notice = ({ notice }) =>
  notice && (
    <div className={`pdNotice ${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>
      <Icon name={notice.type === "error" ? "alert" : "clock"} size={20} />
      <div>
        <div className="pdNoticeTitle">{notice.title}</div>
        <div className="pdNoticeText">{notice.text}</div>
      </div>
    </div>
  );

const Recordings = observer(() => {
  const { recordings } = powerDialerStore;
  const [recorder, setRecorder] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [playing, setPlaying] = useState("");
  const [pending, setPending] = useState(null);
  const [pendingName, setPendingName] = useState("");
  const [notice, setNotice] = useState(null);
  const audioRef = useRef(null);
  const fileRef = useRef(null);
  const secondsRef = useRef(0);

  const save = async (file, name, durationSec) => {
    const ok = await powerDialerStore.uploadRecording(file, name, durationSec);
    ok && toasterState.setToastMsg("Recording saved", "success");
    return ok;
  };

  const stopRecording = async () => {
    if (!recorder) return;
    setPending({ blob: recorder.stop(), seconds: secondsRef.current });
    setPendingName(`Recording ${recordings.length + 1}`);
    setRecorder(null);
  };

  const cancelRecording = () => {
    recorder && recorder.stop();
    setRecorder(null);
  };

  const savePending = async () => {
    const name = pendingName.trim();
    if (!name) return;
    setPending(null);
    await save(pending.blob, name, pending.seconds);
  };

  const startRecording = async () => {
    setNotice(null);
    try {
      const rec = await startWavRecording();
      secondsRef.current = 0;
      setSeconds(0);
      setRecorder(rec);
    } catch (e) {
      toasterState.setToastMsg("Microphone access is needed to record.", "failure");
    }
  };

  useEffect(() => {
    if (!recorder) return undefined;
    const t = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
      if (secondsRef.current >= MAX_SECONDS) stopRecording();
    }, 1000);
    return () => clearInterval(t);
  }, [recorder]);

  const onUpload = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    setNotice(null);
    if (!file) return;
    if (!/\.(mp3|wav)$/i.test(file.name)) {
      toasterState.setToastMsg("Use an MP3 or WAV file", "failure");
      return;
    }
    // 0 = length unknown (metadata error, or no event within 3s, e.g. in a background tab).
    const url = URL.createObjectURL(file);
    const duration = await new Promise(resolve => {
      const audio = new Audio(url);
      const timer = setTimeout(() => resolve(0), 3000);
      audio.onloadedmetadata = () => {
        clearTimeout(timer);
        resolve(audio.duration || 0);
      };
      audio.onerror = () => {
        clearTimeout(timer);
        resolve(0);
      };
    });
    URL.revokeObjectURL(url);
    const isWav = /\.wav$/i.test(file.name);
    if (!duration && !isWav) {
      toasterState.setToastMsg("Couldn't read the MP3's length. Try again, or upload a WAV.", "failure");
      return;
    }
    const length = Math.ceil(duration);
    if (length > MAX_SECONDS) {
      setNotice({
        type: "error",
        title: "Too long to upload",
        text: `${file.name} is ${fmt(length)}. Trim it to ${fmt(MAX_SECONDS)} or less and try again.`
      });
      return;
    }
    // WAV with unknown length: the server reads it from the file header.
    const ok = await save(file, file.name.replace(/\.(mp3|wav)$/i, ""), length || undefined);
    if (ok && length > 30) {
      setNotice({
        type: "warn",
        title: "Uploaded, but a bit long",
        text: `${file.name} is ${fmt(length)}. It will work; under 0:30 tends to get listened to.`
      });
    }
  };

  const play = r => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing === r.id) {
      audio.pause();
      setPlaying("");
      return;
    }
    audio.src = r.url;
    audio.play();
    setPlaying(r.id);
  };

  const remove = r => {
    confirmationPopupState.setPopupValues({
      content: `Delete “${r.name}”?`,
      actionBtnText: "Delete",
      callback: async () => {
        confirmationPopupState.setShowPopup(false);
        await powerDialerStore.deleteRecording(r.id);
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  return (
    <div className="pdCard pdRecordings">
      <div className="pdRecsHead">
        <h3>My recordings</h3>
        <p>Only you can drop these. Your default plays on auto drop.</p>
      </div>
      <audio ref={audioRef} onEnded={() => setPlaying("")} />
      {!recordings.length && !recorder && !pending && (
        <div className="pdRecsEmpty">No recordings yet. Record one or upload an MP3 or WAV.</div>
      )}
      {recordings.length > 0 && (
        <div className="pdRecList">
          {recordings.map(r => (
            <div className="pdRecRow" key={r.id}>
              <button
                type="button"
                className="pdRecPlay"
                aria-label={`${playing === r.id ? "Pause" : "Play"} ${r.name}`}
                onClick={() => play(r)}
              >
                <Glyph name={playing === r.id ? "pause" : "play"} />
              </button>
              <span className="pdRecName" title={r.name}>
                {r.name}
              </span>
              <LengthBadge seconds={r.durationSec || 0} />
              {r.isDefault ? (
                <span className="pdRecDefault">Default</span>
              ) : (
                <button type="button" className="pdRecSetDefault" onClick={() => powerDialerStore.setDefaultRecording(r.id)}>
                  Set default
                </button>
              )}
              <button type="button" className="pdRecDelete" aria-label={`Delete ${r.name}`} onClick={() => remove(r)}>
                <Icon name="trash" size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
      {recorder && <RecordingProgress seconds={seconds} onStop={stopRecording} onCancel={cancelRecording} />}
      {pending && (
        <div className="pdRecSave">
          <LengthBadge seconds={pending.seconds} />
          <input
            className="pdRecNameInput"
            aria-label="Recording name"
            value={pendingName}
            autoFocus
            onChange={e => setPendingName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && savePending()}
          />
          <button type="button" className="pdRecBtn primary" onClick={savePending}>
            Save
          </button>
          <button type="button" className="pdRecBtn" onClick={() => setPending(null)}>
            Discard
          </button>
        </div>
      )}
      <Notice notice={notice} />
      {!recorder && !pending && (
        <div className="pdRecActions">
          <div className="pdRecActionBtns">
            <button type="button" className="pdRecBtn outline" onClick={startRecording}>
              <Icon name="mic" />Record new
            </button>
            <button type="button" className="pdRecBtn outline" onClick={() => fileRef.current?.click()}>
              <Icon name="upload" />Upload
            </button>
          </div>
          <p>MP3 or WAV, up to 90 sec. Aim for 30 sec or less, since short messages get heard.</p>
        </div>
      )}
      <input ref={fileRef} type="file" accept=".mp3,.wav,audio/mpeg,audio/wav" hidden onChange={onUpload} />
    </div>
  );
});

const Voicemail = observer(() => {
  const { settings, saveSettings, loadSettings } = powerDialerStore;

  useEffect(() => {
    loadSettings();
  }, []);

  return (
    <div className="pdSettings">
      <div className="pdCard">
        <h3>Voicemail drop</h3>
        <Toggle
          id="detectVoicemail"
          label="Detect voicemail"
          desc="Knows when a call wasn’t picked up and went to voicemail. A live person hears a short pause before connecting."
          value={settings.detectVoicemail}
          onChange={v => saveSettings({ detectVoicemail: v })}
        />
        <div className="pdRow">
          <div>
            When a call reaches voicemail
            <div className="pdDesc">You get a Drop voicemail button, in click-to-call and the power dialer</div>
          </div>
        </div>
        <Toggle
          id="autoVoicemailDrop"
          label="Auto voicemail drop"
          desc="Drops your default message without asking"
          value={settings.autoVoicemailDrop}
          onChange={v => saveSettings({ autoVoicemailDrop: v })}
        />
        <Toggle
          id="includeMobileInAutoDrop"
          label="Include mobile numbers in auto drop"
          desc="Prerecorded messages to US mobiles need prior consent"
          value={settings.includeMobileInAutoDrop}
          onChange={v => saveSettings({ includeMobileInAutoDrop: v })}
        />
        <div className="pdRow">
          <div>
            Logged as
            <div className="pdDesc">
              Dropped: Left voicemail, note “Voicemail dropped” or “Auto voicemail dropped”. Skipped: Voicemail, none left
            </div>
          </div>
        </div>
      </div>
      <Recordings />
    </div>
  );
});

export default Voicemail;
